/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Center, Flex, Heading, Spinner, Stack, Text, useColorModeValue, useToast } from '@chakra-ui/react';
import { updateGlobalSettingsState } from 'app/slice/globalSettingsSlice';
import axios from 'axios';
import { publicIpv4 } from 'public-ip';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import ProtectedRoute from './Auth/ProtectedRoute';
import tawktoConfig from './TawktoConfig';
import { Loader } from './admin/Loader/Loader';
import { basicToken, config } from './config';
import defaultFaviconIco from './img/favicon.ico';
import useDomain from './service/DomainHook';
import MetaConfiguration from './service/MetaConfiguration';
import DomainUtils from './service/utils/DomainUtils';
import useDocumentTitle from './service/utils/updateDocumentTitle';
import { isTokenExpired } from 'service/utils/tokenVerify';
import { jwtDecode } from "jwt-decode";
import { accessLocalStorage } from 'service/utils/accessLocalStorage';
import Auth from 'Auth/Auth';
import { logoutHandler } from 'components/header/Header';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

// Admin Dashboard
const DashboardRouter = lazy(() => import('./admin/DashboardRouter'));
const SubscriptionPrice = lazy(() => import('./admin/SubscriptionPrice/Price'));

// Website component
const LandingPage = lazy(() => import('./web/LandingPage'));
const Footer = lazy(() => import('./components/footer/Footer'));
const Error404 = lazy(() => import('./web/Error404'));
const AuthWarning = lazy(() => import('./Auth/AuthWarning'));
const PageRenderComponent = lazy(() =>
  import('./web/builderRender/PageRenderComponent')
);
const ForgetPassword = lazy(() => import('./components/ForgetPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const StartTrial = lazy(() => import('./components/startTrial/StartTrial'));
const Signin = lazy(() => import('./components/Signin/Signin'));
const PricingPage = lazy(() => import('./web/Price/Pricing'));
const BuilderPreview = lazy(() => import('./web/BuilderPreview'));

// Blog Component
const BlogList = lazy(() =>
  import('./web/DynamicMenuComponents/Blog/BlogList')
);
const BlogDetails = lazy(() =>
  import('./web/DynamicMenuComponents/Blog/BlogDetails')
);
const BlogTagList = lazy(() =>
  import('./web/DynamicMenuComponents/Blog/BlogTagList')
);

// Product Component
const productByTag = lazy(() =>
  import('./web/DynamicMenuComponents/Product/ByTag/ProductByTag')
);
const ProductByVendor = lazy(() =>
  import('./web/DynamicMenuComponents/Product/ProductByVendor')
);
const ProductByVendorCategory = lazy(() =>
  import('./web/DynamicMenuComponents/Product/ProductByVendorCategory')
);
const VendorRouter = lazy(() => import('./vendorAdmin/VendorRouter'));

// Rentmy Product/package details
const RentMyProductDetails = lazy(() =>
  import('./web/rentmy/RentMyProductDetails')
);
const RentMyPackageDetails = lazy(() =>
  import('./web/rentmy/RentMyPackageDetails')
);
const RentMyCart = lazy(() => import('./web/rentmy/RentMyCart'));
const RentMyCheckoutWrapper = lazy(() =>
  import('./web/rentmy/RentMyCheckoutWrapper')
);
const RentMyCheckoutSuccess = lazy(() =>
  import('./web/rentmy/RentMyCheckoutSuccess')
);

function App() {
  const mediaUrl = config.mediaUrl;
  const baseURL = `${config?.baseUrl}/api/v2`; //`http://api.${window?.location?.host}/v1` //`${config?.baseUrl}${config?.version}`;
  const hasSubscriptionExpired =
    localStorage.getItem('hasSubscriptionExpired') === 'true';
  const dispatch = useDispatch();

  const [hasSubDomain] = useDomain();
  const location = useLocation();
  useDocumentTitle('Admin Dashboard');

  const [inValidDomain, setInValidDomain] = useState(false);
  const [loader, setLoader] = useState(true);
  const [globalSettings, setGlobalSettings] = useState({
    favIcon: '',
    logo: '',
    showBlog: false,
    theme: {
      color: {
        accent: '#319795',
        primary: '#2D3748',
        secondary: '#718096',
      },
    },
  });

  useEffect(() => tawktoConfig(), []);

  // Set theme to localhost
  useEffect(async () => {
    // set default viewport
    localStorage.setItem("viewport", 'base');
    //For common headers to all API
    let publicIp = await publicIpv4();
    axios.defaults.headers.common['X-Forwarded-For'] = publicIp;
    axios.defaults.headers.common['X-Real-Ip'] = publicIp;

    //Api for global settings
    let api = `/globalSettings?adminType=SA&limit=1&fields=theme,favicon,showSignIn`;
    if (hasSubDomain) {
      const [franchisee] = await DomainUtils();
      api = `/globalSettings?adminType=CA&franchiseeId=${franchisee}&limit=1&fields=theme,shopLayout,pReview,favicon,showShop,showSignIn,advanceSeoSettings`;
    }

    axios({
      url: api,
      method: 'get',
      baseURL: baseURL,
      headers: { Authorization: basicToken },
    })
      .then((res) => {
        const customTheme = {
          color: {
            accent: '#319795',
            primary: '#2D3748',
            secondary: '#718096',
          },
          font: ''
        };
        let data = res.data.data;
        const activeBlogPage = res?.data?.seo?.activeBlogPage?.genericSettings?.pageInfo ?? null

        // console.log('data color',data);
        if (data.length === 0) {
          localStorage.setItem('theme', JSON.stringify(customTheme));
          // setThemeUpdated(true);
        } else {
          data = data[0];
          if (data?.theme?.color) {
            localStorage.setItem('theme', JSON.stringify(data.theme));
          } else {
            localStorage.setItem('theme', JSON.stringify(customTheme));
          }
          // localStorage.setItem('shop', data.shopLayout);
          // localStorage.setItem('pReview', data.pReview);
          // if(data.hasOwnProperty('showShop')) {
          //   localStorage.setItem('showShop', data.showShop);
          // }
          localStorage.setItem('showSignIn', data?.showSignIn);
          dispatch(updateGlobalSettingsState({ ...data, activeBlogPage }));

          setGlobalSettings(data);
        }
      })
      .catch((err) => console.log(err)).finally(() => {

        // if (isTokenExpired(token)) {
        //   toast({
        //     title: 'Session expired',
        //     description: 'Please sign in again',
        //     duration: 2000
        //   })
        // logoutHandler(2000)
        // }
      });
  }, []);

  useEffect(async () => {
    if (hasSubDomain) {
      const [franchisee] = await DomainUtils();
      setLoader(false);
      if (!franchisee) {
        setInValidDomain(true);
      }
    } else {
      setLoader(false);
    }
  }, []);

  const toast = useToast();
  const history = useHistory();

  const token = accessLocalStorage('token');


  return (
    <Suspense fallback={<Loader />}>
      {
        loader ?
          <Loader />
          :
          (hasSubDomain && inValidDomain) ?
            <Error404 />
            :
            <>
              {token && isTokenExpired(token) ?
                <Flex
                  minH={'100vh'}
                  align={'center'}
                  justify={'center'}

                >
                  <Stack
                    spacing={4}
                    w={'400px'}
                    maxW={'sm'}
                    bg={'white'}
                    rounded={'xl'}
                    boxShadow={'lg'}
                    p={6}
                    my={10}>
                    <Center>
                      <Stack>
                        <Heading lineHeight={1.1} fontSize={{ base: '2xl', md: '3xl' }}>
                          Browsing Session is expired!
                        </Heading>
                        <Text>Please Sign out and log in again</Text>
                      </Stack>
                    </Center>


                    <Stack spacing={6}>
                      <Button
                        onClick={() => {
                          logoutHandler()
                        }}
                        bg={'blue.400'}
                        color={'white'}
                        _hover={{
                          bg: 'blue.500',
                        }}>
                        Sign Out
                      </Button>
                    </Stack>
                  </Stack>
                </Flex>
                : <>
                  <Helmet>
                    {globalSettings?.favicon ? (
                      // console.log('favIcon:', `${mediaUrl}/${globalSettings?.favicon?.filePath}`)
                      <link
                        rel="icon"
                        type="image/png"
                        // href={favIcon}
                        href={`${mediaUrl}/${globalSettings?.favicon?.filePath}`}
                        sizes="16x16"
                      />
                    ) : (
                      <link
                        rel="icon"
                        type="image/png"
                        href={defaultFaviconIco}
                        sizes="16x16"
                      />
                    )}
                  </Helmet>

                  {/* <GoogleAnalyticsService /> */}
                  <MetaConfiguration />
                  {/* {
              !['admin', 'business'].includes(
                location.pathname?.split('/')[1]
              ) && <Navigation globalSettingsData={globalSettings} />
            } */}

                  {/* <Suspense fallback={<Loader />}> */}
                  {/* <Suspense fallback={<div>Loading...</div>}> */}
                  <main>
                    <Switch>
                      {/* <Route exact path="/" component={LandingPage} /> */}
                      <Route exact path="/" component={Signin} />
                      <ProtectedRoute
                        role={['BUSINESSADMIN']}
                        path="/business"
                        component={VendorRouter}
                      />

                      <ProtectedRoute
                        role={[
                          'SUPERADMIN',
                          'ADMIN',
                          'SUBSCRIPTIONOWNER',
                          'SUBSCRIBERADMIN',
                        ]}
                        path={'/admin'}
                        component={DashboardRouter}
                      />

                      {/* { 
                    hasSubscriptionExpired && hasSubDomain 
                    ? 
                      <> 
                        {console.log('executed this franchisee', SubscriptionPrice)}

                        <ProtectedRoute
                          role={['SUBSCRIPTIONOWNER', 'SUBSCRIBERADMIN']}
                          path={"/admin/subscription-price"}

                          component={SubscriptionPrice}
                        />
                      </>
                    :
                      <ProtectedRoute
                        role={['SUPERADMIN', 'ADMIN', 'SUBSCRIPTIONOWNER', 'SUBSCRIBERADMIN']}
                        path={"/admin"}
                        component={DashboardRouter}
                      />
                  }   */}

                      <Route path="/warning" component={AuthWarning} />
                      {/* <Route path="/" */}
                      <Route path="/forget-password" component={ForgetPassword} />
                      <Route path="/reset-password" component={ResetPassword} />

                      <Route
                        path="/page/:pageRoute"
                        component={PageRenderComponent}
                      />
                      <Route path="/start-trial" component={StartTrial} />
                      <Route path="/signin" component={Signin} />

                      <Route exact path="/blog" component={BlogList} />
                      <Route path="/blog/tag/:slug" component={BlogTagList} />
                      <Route path="/blog/post/:slug" component={BlogDetails} />
                      <Route
                        exact
                        path={`/:type/preview/:builderId`}
                        component={BuilderPreview}
                      />

                      {!hasSubDomain && (
                        <>
                          <Switch>
                            <Route path="/pricing" component={PricingPage} />
                          </Switch>
                        </>
                      )}

                      {hasSubDomain && (
                        <>
                          <Switch>
                            <Route
                              exact
                              path="/rentmy/product/:id/:slug"
                              component={RentMyProductDetails}
                            />
                            <Route
                              exact
                              path="/rentmy/package/:id/:slug"
                              component={RentMyPackageDetails}
                            />
                            <Route
                              exact
                              path="/rentmy/checkout/complete/:id"
                              component={RentMyCheckoutSuccess}
                            />
                            <Route
                              exact
                              path="/rentmy/checkout/single-page-checkout"
                              component={RentMyCheckoutWrapper}
                            />
                            <Route
                              exact
                              path="/rentmy/cart/:id"
                              component={RentMyCart}
                            />
                            <Route
                              exact
                              path="/vendor/:slug"
                              component={ProductByVendor}
                            />
                            <Route
                              exact
                              path="/vendor/tag/:slug"
                              component={productByTag}
                            />
                            <Route
                              exact
                              path="/vendor/category/:slug"
                              component={ProductByVendorCategory}
                            />
                            <Route exact path="page/*" component={Error404} />
                            <Route exact path="/*" component={Error404} />
                          </Switch>
                        </>
                      )}
                      <Route exact path="/*" component={Error404} />
                    </Switch>
                  </main>
                  {/* {!['admin', 'business'].includes(
                location.pathname?.split('/')[1]
              ) && <Footer />} */}
                  {/* </Suspense> */}
                </>
              }
            </>
      }
    </Suspense>
  )

  
  // if (hasSubDomain && inValidDomain) {
  //   return <Error404 />;
  // } else {
  //   if (loader) {
  //     // if (loader) {
  //     return (
  //       <Center h="100vh">
  //         <Spinner color="gray.600" size="lg" />
  //       </Center>
  //     );


  //   } else {
  //     return (
  //       <>
  //         {token && isTokenExpired(token) ?
  //           <Flex
  //             minH={'100vh'}
  //             align={'center'}
  //             justify={'center'}

  //           >
  //             <Stack
  //               spacing={4}
  //               w={'400px'}
  //               maxW={'sm'}
  //               bg={'white'}
  //               rounded={'xl'}
  //               boxShadow={'lg'}
  //               p={6}
  //               my={10}>
  //               <Center>
  //                 <Stack>
  //                   <Heading lineHeight={1.1} fontSize={{ base: '2xl', md: '3xl' }}>
  //                     Browsing Session is expired!
  //                   </Heading>
  //                   <Text>Please Sign out and log in again</Text>
  //                 </Stack>
  //               </Center>


  //               <Stack spacing={6}>
  //                 <Button
  //                   onClick={() => {
  //                     logoutHandler()
  //                   }}
  //                   bg={'blue.400'}
  //                   color={'white'}
  //                   _hover={{
  //                     bg: 'blue.500',
  //                   }}>
  //                   Sign Out
  //                 </Button>
  //               </Stack>
  //             </Stack>
  //           </Flex>
  //           : <>
  //             <Helmet>
  //               {globalSettings?.favicon ? (
  //                 // console.log('favIcon:', `${mediaUrl}/${globalSettings?.favicon?.filePath}`)
  //                 <link
  //                   rel="icon"
  //                   type="image/png"
  //                   // href={favIcon}
  //                   href={`${mediaUrl}/${globalSettings?.favicon?.filePath}`}
  //                   sizes="16x16"
  //                 />
  //               ) : (
  //                 <link
  //                   rel="icon"
  //                   type="image/png"
  //                   href={defaultFaviconIco}
  //                   sizes="16x16"
  //                 />
  //               )}
  //             </Helmet>

  //             {/* <GoogleAnalyticsService /> */}
  //             <MetaConfiguration />
  //             {/* {
  //             !['admin', 'business'].includes(
  //               location.pathname?.split('/')[1]
  //             ) && <Navigation globalSettingsData={globalSettings} />
  //           } */}

  //             <Suspense fallback={<Loader />}>
  //               {/* <Suspense fallback={<div>Loading...</div>}> */}
  //               <main>
  //                 <Switch>
  //                   {/* <Route exact path="/" component={LandingPage} /> */}
  //                   <Route exact path="/" component={Signin} />
  //                   <ProtectedRoute
  //                     role={['BUSINESSADMIN']}
  //                     path="/business"
  //                     component={VendorRouter}
  //                   />

  //                   <ProtectedRoute
  //                     role={[
  //                       'SUPERADMIN',
  //                       'ADMIN',
  //                       'SUBSCRIPTIONOWNER',
  //                       'SUBSCRIBERADMIN',
  //                     ]}
  //                     path={'/admin'}
  //                     component={DashboardRouter}
  //                   />

  //                   {/* { 
  //                   hasSubscriptionExpired && hasSubDomain 
  //                   ? 
  //                     <> 
  //                       {console.log('executed this franchisee', SubscriptionPrice)}

  //                       <ProtectedRoute
  //                         role={['SUBSCRIPTIONOWNER', 'SUBSCRIBERADMIN']}
  //                         path={"/admin/subscription-price"}

  //                         component={SubscriptionPrice}
  //                       />
  //                     </>
  //                   :
  //                     <ProtectedRoute
  //                       role={['SUPERADMIN', 'ADMIN', 'SUBSCRIPTIONOWNER', 'SUBSCRIBERADMIN']}
  //                       path={"/admin"}
  //                       component={DashboardRouter}
  //                     />
  //                 }   */}

  //                   <Route path="/warning" component={AuthWarning} />
  //                   {/* <Route path="/" */}
  //                   <Route path="/forget-password" component={ForgetPassword} />
  //                   <Route path="/reset-password" component={ResetPassword} />

  //                   <Route
  //                     path="/page/:pageRoute"
  //                     component={PageRenderComponent}
  //                   />
  //                   <Route path="/start-trial" component={StartTrial} />
  //                   <Route path="/signin" component={Signin} />

  //                   <Route exact path="/blog" component={BlogList} />
  //                   <Route path="/blog/tag/:slug" component={BlogTagList} />
  //                   <Route path="/blog/post/:slug" component={BlogDetails} />
  //                   <Route
  //                     exact
  //                     path={`/:type/preview/:builderId`}
  //                     component={BuilderPreview}
  //                   />

  //                   {!hasSubDomain && (
  //                     <>
  //                       <Switch>
  //                         <Route path="/pricing" component={PricingPage} />
  //                       </Switch>
  //                     </>
  //                   )}

  //                   {hasSubDomain && (
  //                     <>
  //                       <Switch>
  //                         <Route
  //                           exact
  //                           path="/rentmy/product/:id/:slug"
  //                           component={RentMyProductDetails}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/rentmy/package/:id/:slug"
  //                           component={RentMyPackageDetails}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/rentmy/checkout/complete/:id"
  //                           component={RentMyCheckoutSuccess}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/rentmy/checkout/single-page-checkout"
  //                           component={RentMyCheckoutWrapper}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/rentmy/cart/:id"
  //                           component={RentMyCart}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/vendor/:slug"
  //                           component={ProductByVendor}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/vendor/tag/:slug"
  //                           component={productByTag}
  //                         />
  //                         <Route
  //                           exact
  //                           path="/vendor/category/:slug"
  //                           component={ProductByVendorCategory}
  //                         />
  //                         <Route exact path="page/*" component={Error404} />
  //                         <Route exact path="/*" component={Error404} />
  //                       </Switch>
  //                     </>
  //                   )}
  //                   <Route exact path="/*" component={Error404} />
  //                 </Switch>
  //               </main>
  //               {/* {!['admin', 'business'].includes(
  //               location.pathname?.split('/')[1]
  //             ) && <Footer />} */}
  //             </Suspense>
  //           </>
  //         }
  //       </>
  //     );
  //   }
  // }
}

export default App;
